import React, { useCallback, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import toast from "react-hot-toast";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AdminLayout from "../admin/AdminDashboardLayout";
import { useEngagementState } from "../../hooks/useEngagementState";
import { useApiMutation } from "../../hooks/useMutation";
import { useShift, useHeartbeat } from "../../hooks/useShift";
import { useShiftEngagements, calculateEngagementStats } from "../../hooks/useShiftEngagements";
import { usePartnerAnalytics } from "../../hooks/usePartnerAnalytics";
import { usePollingLocations } from "../../hooks/usePollingLocations";
import axios from "axios";
import { motion } from "framer-motion";
import Button from "../../utils/Button";
import Input from "../../utils/Input";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ElectionReminders from "../../components/ElectionReminders";
import VoterPledge from "../../components/VoterPledge";
import ConfirmationSuccess from "../../components/ConfirmationSuccess";
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MapIcon from '@mui/icons-material/Map';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SecurityIcon from '@mui/icons-material/Security';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import MarkUnreadChatAltIcon from '@mui/icons-material/MarkUnreadChatAlt';
import talk from '../../assets/images/talk2.jpg';
import { Mail } from "@mui/icons-material";
import ElectionDates from "../../components/ElectionDates";
import { useApiQuery } from "../../hooks/useApiQuery";
import { US_STATES } from "../../utils/Constant";
import { useDashboardRole } from "../../hooks/useDashboardRole";

const states = [
	"Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware",
	"Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
	"Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi",
	"Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico",
	"New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania",
	"Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont",
	"Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
];


interface ElectionData {
	elections: Array<{
		id: number;
		googleElectionId: string;
		name: string;
		electionDay: string;
		ocdDivisionId: string;
		electionType: 'GENERAL' | 'PRIMARY' | 'SPECIAL';
		stateCode: string;
		electionHours: string | null;
		registrationDeadline: string | null;
		mailRequestDeadline: string | null;
		mailReturnDeadline: string | null;
		earlyVotingStartDate: string | null;
		earlyVotingEndDate: string | null;
		voterIdRequirement: string | null;
		felonyVotingRules: string | null;
		electionInfoUrl: string;
		electionRegistrationUrl: string;
		electionRegistrationConfirmationUrl: string;
		absenteeVotingInfoUrl: string;
		votingLocationFinderUrl: string;
		ballotInfoUrl: string | null;
		electionRulesUrl: string;
		officialStateWebsite: string;
		voterInfoEnriched: boolean;
		createdAt: string;
		updatedAt: string;
	}>;
	state: {
		'SOS/DataEndpoint': string;
		'RegistrationLookupUrl': string;
		'VoteByMailUrl': string;
		'VoteByMailOnlineAllowed': string;
	};
}

interface ElectionApiResponse {
	data: ElectionData;
}


delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
	iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
	iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
	shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

export default function CanvasserDashboard() {
	return <DashboardContent />;
}

function DashboardContent() {
  const { dashboardRole } = useDashboardRole();
	type RegistrationFieldErrors = Partial<Record<
		'firstName' | 'lastName' | 'state' | 'zipcode' | 'city' | 'address' | 'phoneNumber' | 'email' | 'dob' | 'gender' | 'reminder',
		string
	>>;

	const {
		isEngagementActive,
		currentStep,
		engagementData,
		updateEngagementData,
		startEngagement,
		nextStep,
		previousStep,
		endEngagement,
		setCurrentStep,
	} = useEngagementState();

	// Helper function to convert state code to state name
	const getStateName = (stateCode: string): string => {
		const state = US_STATES.find(s => s.code === stateCode);
		return state?.name || '';
	};

	// Helper function to get state code (returns the code directly)
	const getStateCode = (stateCode: string): string => {
		return stateCode;
	};

	const { currentShift, isLoading: shiftLoading } = useShift();

	const { data: engagementsData, isLoading: engagementsLoading } = useShiftEngagements(currentShift?.id);



	const { data: partnerAnalyticsData, isLoading: analyticsLoading } = usePartnerAnalytics();
	const partnerAnalytics = useMemo(() => {
		if (!partnerAnalyticsData?.data?.analytics) {
			return {
				totalLeads: 0,
				registeredLeads: 0,
				unregisteredLeads: 0,
				remindersSet: 0,
			};
		}
		return partnerAnalyticsData.data.analytics;
	}, [partnerAnalyticsData]);

	const [registrationResult, setRegistrationResult] = useState<any>(null);
	const [showReturnFlow, setShowReturnFlow] = useState(false);
	const [registrationCompleted, setRegistrationCompleted] = useState(false);
	const [currentFlow, setCurrentFlow] = useState<'polling' | 'reminders' | 'dates' | null>(null);

	const [showRegistrationStatusPage, setShowRegistrationStatusPage] = useState(false);
	const [selectedRegistrationStatus, setSelectedRegistrationStatus] = useState<string | null>(null);
	const [showNotRegisteredModal, setShowNotRegisteredModal] = useState(false);
	const [showCompletionCheckModal, setShowCompletionCheckModal] = useState(false);
	const [showContactOptIn, setShowContactOptIn] = useState(false);
	const [contactOptInChecked, setContactOptInChecked] = useState(false);

	const [showVBMCompletionModal, setShowVBMCompletionModal] = useState(false);
	const [showVBMOptInModal, setShowVBMOptInModal] = useState(false);
	const [showVBMRetryModal, setShowVBMRetryModal] = useState(false);
	const [formErrors, setFormErrors] = useState<RegistrationFieldErrors>({});
	const [vbmOptIns, setVbmOptIns] = useState({
		mailBallotReminders: false,
		ballotTracking: false,
		returnDeadlineReminders: false
	});

	const mapBackendFieldToFormField = useCallback((fieldKey: string): keyof RegistrationFieldErrors | null => {
		const fieldMap: Record<string, keyof RegistrationFieldErrors> = {
			firstName: "firstName",
			lastName: "lastName",
			state: "state",
			zipcode: "zipcode",
			zip: "zipcode",
			city: "city",
			address: "address",
			phone: "phoneNumber",
			phoneNumber: "phoneNumber",
			email: "email",
			dob: "dob",
			gender: "gender",
			reminder: "reminder",
		};

		return fieldMap[fieldKey] || null;
	}, []);

	const getAgeFromDob = useCallback((dob: string): number => {
		if (!dob) return 0;
		const birthDate = new Date(dob);
		if (Number.isNaN(birthDate.getTime())) return 0;

		const today = new Date();
		let age = today.getFullYear() - birthDate.getFullYear();
		const hasBirthdayPassedThisYear =
			today.getMonth() > birthDate.getMonth() ||
			(today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());

		if (!hasBirthdayPassedThisYear) {
			age -= 1;
		}

		return age;
	}, []);

	const validateRegistrationForm = useCallback((): RegistrationFieldErrors => {
		const errors: RegistrationFieldErrors = {};

		if (!engagementData.firstName?.trim()) errors.firstName = "First name is required";
		if (!engagementData.lastName?.trim()) errors.lastName = "Last name is required";
		if (!engagementData.state?.trim()) errors.state = "State is required";
		if (!engagementData.zipcode?.trim()) errors.zipcode = "Zipcode is required";
		if (!engagementData.city?.trim()) errors.city = "City is required";
		if (!engagementData.address?.trim()) errors.address = "Home address is required";
		if (!engagementData.email?.trim()) errors.email = "Email address is required";
		if (!engagementData.dob?.trim()) {
			errors.dob = "Date of birth is required";
		} else {
			const age = getAgeFromDob(engagementData.dob);
			if (age < 16) {
				errors.dob = "Voter must be 16 years old to be eligible";
			}
		}
		if (!engagementData.gender?.trim()) errors.gender = "Gender is required";

		return errors;
	}, [engagementData, getAgeFromDob]);

	const requiredFieldMessages: Partial<Record<keyof RegistrationFieldErrors, string>> = {
		firstName: "First name is required",
		lastName: "Last name is required",
		state: "State is required",
		zipcode: "Zipcode is required",
		city: "City is required",
		address: "Home address is required",
		email: "Email address is required",
		gender: "Gender is required",
	};

	const getFieldError = (name: keyof RegistrationFieldErrors, value: string | boolean): string => {
		if (name === "dob") {
			const dobValue = String(value).trim();
			if (!dobValue) return "Date of birth is required";
			return getAgeFromDob(dobValue) < 16 ? "Voter must be 16 years old to be eligible" : "";
		}

		const requiredMessage = requiredFieldMessages[name];
		if (!requiredMessage) return "";
		return String(value).trim() ? "" : requiredMessage;
	};

	const { data: pollingLocationsData, isLoading: isPollingLoading, error: pollingError, refetch: refetchPollingLocations } = usePollingLocations({
		address: engagementData.address,
		city: engagementData.city,
		state: getStateName(engagementData.state),
		zipcode: engagementData.zipcode,
		enabled: currentStep === 3 && currentFlow === 'polling' && !!(engagementData.address && engagementData.city && engagementData.state && engagementData.zipcode),
	});

	const pollingLocations = pollingLocationsData?.data?.pollingLocations || [];

	const registrationCheckMutation = useApiMutation({
		onSuccess: result => {
			setFormErrors({});
			setRegistrationResult(result);

			if (result.redirectUrl) {
				updateEngagementData({ registrationStatus: "not_registered" });
				nextStep();
			} else {
				updateEngagementData({ registrationStatus: "registered" });
				nextStep();
				nextStep();
			}
		},
		onError: (error: any) => {
			console.log(error);
			const backendValidationErrors = error?.response?.data?.data;
			const normalizedErrors: RegistrationFieldErrors = {};
			let hasValidationErrors = false;

			if (backendValidationErrors && typeof backendValidationErrors === "object") {
				Object.entries(backendValidationErrors).forEach(([fieldKey, message]) => {
					const fieldName = mapBackendFieldToFormField(fieldKey);
					const messageText = typeof message === "string" ? message : "Invalid value";

					if (fieldName) {
						normalizedErrors[fieldName] = messageText;
						hasValidationErrors = true;
					}

					toast.error(messageText);
				});
			}

			if (hasValidationErrors) {
				setFormErrors(normalizedErrors);
				return;
			}

			const errorMessage = error?.response?.data?.message || error?.message || 'Registration check failed. Please try again.';
			toast.error(errorMessage);
		},
	});

	const registrationStatusUpdateMutation = useApiMutation({
		onSuccess: () => {
			console.log("Registration status updated successfully");
		},
		onError: (error: any) => {
			const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update registration status.';
			toast.error(errorMessage);
		},
	});

	const pledgeToVoteMutation = useApiMutation({
		onSuccess: () => {
			toast.success("Pledge saved successfully");
		},
		onError: (error: any) => {
			const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save pledge to vote.';
			toast.error(errorMessage);
		},
	});

	const contactOptInMutation = useApiMutation<{
		message?: string;
	}>({});



	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
			const { name, value, type } = e.target;
			const nextValue = type === "checkbox" ? (e.target as HTMLInputElement).checked : value;
			updateEngagementData({
				[name]: nextValue,
			});

			const formFieldName = name as keyof RegistrationFieldErrors;
			setFormErrors(prev => {
				if (!prev[formFieldName]) {
					return prev;
				}

				return {
					...prev,
					[formFieldName]: getFieldError(formFieldName, nextValue),
				};
			});
		},
		[updateEngagementData, getAgeFromDob]
	);

	const handleGender = useCallback(
		(gender: string) => {
			updateEngagementData({ gender });
			setFormErrors(prev => ({
				...prev,
				gender: getFieldError("gender", gender),
			}));
		},
		[updateEngagementData]
	);

	const handleRegistrationCheck = useCallback(() => {
		const validationErrors = validateRegistrationForm();
		if (Object.keys(validationErrors).length > 0) {
			setFormErrors(validationErrors);
			if (validationErrors.dob === "Voter must be 16 years old to be eligible") {
				toast.error("Voter must be 16 years old to be eligible");
			} else {
				toast.error("Please complete all required fields before checking status.");
			}
			return;
		}

		setFormErrors({});
		const trimmedPhone = engagementData.phoneNumber?.trim();
		const submissionData = {
			url: "/registration-check",
			method: "POST",
			data: {
				firstName: engagementData.firstName.trim(),
				lastName: engagementData.lastName.trim(),
				address: engagementData.address.trim(),
				city: engagementData.city.trim(),
				state: getStateName(engagementData.state),
				zipcode: engagementData.zipcode.trim(),
				...(trimmedPhone ? { phone: trimmedPhone } : {}),
				email: engagementData.email.trim(),
				dob: engagementData.dob,
				gender: engagementData.gender ? engagementData.gender.toUpperCase() : "",
				reminder: engagementData.reminder,
			},
		};

		registrationCheckMutation.mutate(submissionData);
	}, [engagementData, registrationCheckMutation, validateRegistrationForm]);

	const handleOpenStateWebsite = useCallback(() => {
		// Open the state website in a new small tab
		if (registrationResult?.redirectUrl) {
			window.open(registrationResult.redirectUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
		}
		// Immediately proceed to the next step after opening the tab
		setRegistrationCompleted(true);
		setShowRegistrationStatusPage(true);
	}, [registrationResult]);


	// Handler for return flow options
	const handleReturnFlowOption = useCallback((option: string) => {
		setShowReturnFlow(false);
		switch (option) {
			case 'polling':
				setCurrentFlow('polling');
				setCurrentStep(3); // Go to polling location (step 3)
				break;
			case 'reminders':
				setCurrentFlow('reminders');
				setCurrentStep(3); // Go to reminders (step 3)
				break;
			case 'dates':
				setCurrentFlow('dates');
				setCurrentStep(3); // Go to dates (step 3)
				break;
			case 'vbm':
				// Open the VBM website in a new small tab
				if (registrationResult?.voteByMailUrl) {
					window.open(registrationResult.voteByMailUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
				}
				// Immediately proceed to the next step after opening the tab
				setShowVBMCompletionModal(true);
				break;
			case 'exit':
				setCurrentFlow(null);
				// Show confirmation success page
				updateEngagementData({ showConfirmation: true });
				setCurrentStep(4); // Go directly to finish step
				break;
		}
	}, [registrationResult]);

	const vbmStatusUpdateMutation = useApiMutation({
		onSuccess: () => {
			console.log('VBM status updated successfully');
		},
		onError: (error: any) => {
			console.error('VBM status update error:', error);
		},
	});

	const handleRegistrationStatusUpdate = useCallback((status: string) => {
		if (registrationResult?.registration?.id) {
			let isRegistered = false;
			let selfReportedStatus = 'NOT_SURE';

			if (status === 'registered') {
				isRegistered = true;
				selfReportedStatus = 'REGISTERED';
			} else if (status === 'not_registered') {
				isRegistered = false;
				selfReportedStatus = 'NOT_REGISTERED';
			} else if (status === 'not_sure') {
				isRegistered = false;
				selfReportedStatus = 'NOT_SURE';
			}

			const submissionData = {
				url: `/registration-check/${registrationResult.registration.id}`,
				method: "PUT",
				data: {
					isRegistered,
					selfReportedStatus
				},
			};
			registrationStatusUpdateMutation.mutate(submissionData);
		}
	}, [registrationResult, registrationStatusUpdateMutation]);

	const handleRegistrationStatusSelection = useCallback((status: string) => {
		setSelectedRegistrationStatus(status);
		if (status === 'registered') {
			handleRegistrationStatusUpdate('registered');
			setShowRegistrationStatusPage(false);
			setShowReturnFlow(true);
		} else if (status === 'not_registered') {
			handleRegistrationStatusUpdate('not_registered');
			setShowRegistrationStatusPage(false);
			setShowNotRegisteredModal(true);
		} else if (status === 'not_sure') {
			handleRegistrationStatusUpdate('not_sure');
			setShowRegistrationStatusPage(false);
			setContactOptInChecked(false);
			setShowContactOptIn(true);
		}
	}, [handleRegistrationStatusUpdate]);

	const handleRegisterOnStateSite = useCallback(() => {
		setShowNotRegisteredModal(false);
		// Open the state website in a new small tab
		if (registrationResult?.redirectUrl) {
			window.open(registrationResult.redirectUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
		}
		// Immediately proceed to the next step after opening the tab
		setShowCompletionCheckModal(true);
	}, [registrationResult]);


	const handleCompletionCheckResponse = useCallback((completed: boolean) => {
		setShowCompletionCheckModal(false);
		if (completed) {
			handleRegistrationStatusUpdate('registered');
			setShowReturnFlow(true);
		} else {
			handleRegistrationStatusUpdate('not_registered');
			setContactOptInChecked(false);
			setShowContactOptIn(true);
		}
	}, [handleRegistrationStatusUpdate]);

	const proceedToReturnFlow = useCallback(() => {
		setShowContactOptIn(false);
		setContactOptInChecked(false);
		setShowReturnFlow(true);
	}, []);

	const handleContactOptInSkip = useCallback(() => {
		proceedToReturnFlow();
	}, [proceedToReturnFlow]);

	const handleContactOptInSubmit = useCallback(() => {
		const checkRegistrationLogId = registrationResult?.registration?.id;

		if (!checkRegistrationLogId) {
			toast.error('Missing registration id for sending the official registration link.');
			proceedToReturnFlow();
			return;
		}

		const trimmedPhone = engagementData.phoneNumber?.trim() || '';
		const trimmedEmail = engagementData.email.trim();
		const contactOptInPayload = {
			checkRegistrationLogId,
			bymail: !!trimmedEmail,
			bysms: !!trimmedPhone,
		};

		contactOptInMutation.mutate({
			url: '/registration-check/send-links',
			method: 'POST',
			data: contactOptInPayload,
		}, {
			onSuccess: () => {
				toast.success("We'll send the official registration link.");
				proceedToReturnFlow();
			},
			onError: (error: any) => {
				const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save contact preference.';
				toast.error(errorMessage);
				proceedToReturnFlow();
			},
		});
	}, [contactOptInMutation, engagementData.email, engagementData.phoneNumber, proceedToReturnFlow, registrationResult]);


	const { data: electionData, isLoading: isElectionLoading, error: electionError } = useApiQuery<ElectionApiResponse>({
		queryKey: ['electionData', engagementData.state],
		request: {
			method: 'GET',
			url: `/elections/state/${getStateCode(engagementData.state)}`,
		},
		enabled: currentStep === 3 && currentFlow === 'dates' && !!engagementData.state,
	});


	const handleVBMCompletionResponse = useCallback((response: string) => {
		setShowVBMCompletionModal(false);

		const statusMap: Record<string, string> = {
			'requested': 'REQUESTED',
			'didnt_complete': 'NOT_COMPLETED',
			'not_eligible': 'NOT_ELIGIBLE'
		};

		const status = statusMap[response];
		if (status && registrationResult?.registration?.id) {
			const submissionData = {
				url: `/vote-by-mail/${registrationResult.registration.id}`,
				method: 'POST',
				data: {
					status: status,
					signOnSource: 'WEB PORTAL'
				}
			};
			vbmStatusUpdateMutation.mutate(submissionData);
		}

		if (response === 'requested') {
			setShowVBMOptInModal(true);
		} else if (response === 'didnt_complete') {
			setShowVBMRetryModal(true);
		} else if (response === 'not_eligible') {
			// Show polling location for not eligible users
			setShowReturnFlow(true)
			nextStep();
		}
	}, [nextStep, registrationResult, vbmStatusUpdateMutation]);

	const handleVBMRetryReturn = useCallback(() => {
		setShowVBMRetryModal(false);
		// Reopen the VBM website in a new small tab
		if (registrationResult?.voteByMailUrl) {
			window.open(registrationResult.voteByMailUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
		}
		// Show the completion modal again after opening the tab
		setShowVBMCompletionModal(true);
	}, [registrationResult]);

	const vbmOptInMutation = useApiMutation({
		onSuccess: (response: { message?: string }) => {
			console.log('VBM opt-in successful');
			toast.success(response?.message || 'VBM opt-in successful');
		},
		onError: (error: unknown) => {
			console.error('VBM opt-in error:', error);
			const errorMessage =
				(error as any)?.response?.data?.message ||
				(error as any)?.message ||
				'VBM opt-in failed';
			toast.error(errorMessage);
		},
	});

	const handleVBMOptInSubmit = useCallback(() => {
		setShowVBMOptInModal(false);

		const enabled = [];
		if (vbmOptIns.mailBallotReminders) enabled.push('MAIL_BALLOT_REMINDERS');
		if (vbmOptIns.ballotTracking) enabled.push('MAIL_BALLOT_TRACKING_INFO');
		if (vbmOptIns.returnDeadlineReminders) enabled.push('MAIL_BALLOT_DEADLINE');

		if (enabled.length > 0) {
			const optInData = {
				url: `/vote-by-mail/${registrationResult?.registration?.id}`,
				method: 'PUT',
				data: {
					reminderOptions: enabled
				}
			};
			vbmOptInMutation.mutate(optInData);
		}

		// Take to pledge to vote stage
		updateEngagementData({ showConfirmation: true });
		nextStep();
		nextStep(); // Skip to step 4
	}, [vbmOptIns, engagementData, nextStep, updateEngagementData, vbmOptInMutation]);


	const handleVBMOptInChange = useCallback((option: string, checked: boolean) => {
		setVbmOptIns(prev => ({
			...prev,
			[option]: checked
		}));
	}, []);

	const handlePledgeSubmit = useCallback(() => {
		if (!engagementData.voterPledge) {
			updateEngagementData({ showConfirmation: true });
			return;
		}

		const checkRegistrationLogId = registrationResult?.registration?.id;

		if (!checkRegistrationLogId) {
			toast.error('Missing registration id for pledge to vote.');
			return;
		}

		pledgeToVoteMutation.mutate({
			url: "/pledge-to-vote",
			method: "POST",
			data: {
				checkRegistrationLogId,
			},
		}, {
			onSuccess: () => {
				updateEngagementData({ showConfirmation: true });
			},
		});
	}, [engagementData.voterPledge, registrationResult, pledgeToVoteMutation, updateEngagementData]);


	if (!isEngagementActive) {
		return (
			<AdminLayout role={dashboardRole}>
				<div className=" mx-auto space-y-8">

					<div className="bg-white rounded-xl p-3 md:p-7">
						<div className="flex items-start justify-between md:gap-10">
							<div className="text-left">
								<h1 className="text-xl font-bold text-gray-900 mb-2">Canvasser Mode</h1>
								<p className="text-gray-400 text-sm">You are currently canvassing for your organisation</p>

								<div className="my-8">
									<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
										<motion.div
											initial={{ opacity: 0, y: 20 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ delay: 0.1 }}
											className="relative group"
										>
											<div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300 opacity-75"></div>
											<div className="relative bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
												<div className="flex justify-between items-start mb-4">
													<div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl text-white shadow-lg">
														<PeopleAltIcon />
													</div>
												</div>
												<h3 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">
													{analyticsLoading || engagementsLoading ? "..." : partnerAnalytics.totalLeads.toLocaleString()}
												</h3>
												<p className="text-sm font-medium text-gray-600">
													Engagements
												</p>
												<div className="mt-4 h-1 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-full overflow-hidden">
													<motion.div
														initial={{ width: 0 }}
														animate={{ width: "75%" }}
														transition={{ delay: 0.5, duration: 0.8 }}
														className="h-full bg-gradient-to-r from-blue-500 to-cyan-600 rounded-full"
													/>
												</div>
											</div>
										</motion.div>

										<motion.div
											initial={{ opacity: 0, y: 20 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ delay: 0.2 }}
											className="relative group"
										>
											<div className="absolute inset-0 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300 opacity-75"></div>
											<div className="relative bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
												<div className="flex justify-between items-start mb-4">
													<div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl text-white shadow-lg">
														<HowToRegIcon />
													</div>
												</div>
												<h3 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">
													{analyticsLoading || engagementsLoading ? "..." : partnerAnalytics.registeredLeads.toLocaleString()}
												</h3>
												<p className="text-sm font-medium text-gray-600">
													Registrations
												</p>
												<div className="mt-4 h-1 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-full overflow-hidden">
													<motion.div
														initial={{ width: 0 }}
														animate={{ width: "60%" }}
														transition={{ delay: 0.6, duration: 0.8 }}
														className="h-full bg-gradient-to-r from-green-500 to-emerald-600 rounded-full"
													/>
												</div>
											</div>
										</motion.div>

										<motion.div
											initial={{ opacity: 0, y: 20 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ delay: 0.3 }}
											className="relative group"
										>
											<div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300 opacity-75"></div>
											<div className="relative bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
												<div className="flex justify-between items-start mb-4">
													<div className="p-3 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl text-white shadow-lg">
														<MarkUnreadChatAltIcon />
													</div>
												</div>
												<h3 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">
													{analyticsLoading || engagementsLoading ? "..." : partnerAnalytics.remindersSet.toLocaleString()}
												</h3>
												<p className="text-sm font-medium text-gray-600">
													Reminders
												</p>
												<div className="mt-4 h-1 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-full overflow-hidden">
													<motion.div
														initial={{ width: 0 }}
														animate={{ width: "85%" }}
														transition={{ delay: 0.7, duration: 0.8 }}
														className="h-full bg-gradient-to-r from-amber-500 to-orange-600 rounded-full"
													/>
												</div>
											</div>
										</motion.div>
									</div>
								</div>
								<div className="text-center">
									<button
										onClick={startEngagement}
										className="w-32 h-32 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center mx-auto mb-6 transition-all duration-200 transform hover:scale-105 shadow-lg"
									>
										<PersonAddIcon style={{ fontSize: 48 }} />
									</button>
									<h2 className="text-2xl font-semibold mb-3">Start Engagement</h2>
									<p className="text-gray-600 max-w-md mx-auto">
										Ready to speak with a voter? Tap the button to start logging details.
									</p>
								</div>
							</div>
							<div className="w-[50%] md:block hidden">

								<img src={talk} alt="people talking" className="w-full rounded-xl h-full mx-auto mb-6" />
							</div>
						</div>
					</div>
				</div>
			</AdminLayout>
		);
	}

	return (
		<AdminLayout role={dashboardRole}>
			<div className="max-w-2xl mx-auto md:p-6">
				{/* Header */}
				<div className="flex items-center justify-between mb-6">
					<div className="flex items-center gap-3">
						<div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
						<span className="text-green-600 font-medium">Active Shift</span>
					</div>
					<Button
						label="End Engagement"
						variant="tertiary"
						size="medium"
						onClick={endEngagement}
					/>
				</div>

				{/* Progress indicator */}
				<div className="mb-8">
					<div className="flex items-center justify-between mb-2">
						<span className="text-sm font-medium text-gray-500">STEP {currentStep} OF 4</span>
						<span className="text-sm font-medium text-gray-900">
							{currentStep === 1 && 'Registration Check'}
							{currentStep === 2 && 'State Registration'}
							{currentStep === 3 && currentFlow === 'polling' && 'Polling Location'}
							{currentStep === 3 && currentFlow === 'reminders' && 'Election Reminders'}
							{currentStep === 3 && currentFlow === 'dates' && 'Voting Dates'}
							{currentStep === 4 && 'Complete'}
						</span>
					</div>
					<div className="w-full bg-gray-200 rounded-full h-2">
						<div
							className="bg-blue-600 h-2 rounded-full transition-all duration-300"
							style={{ width: `${(currentStep / 4) * 100}%` }}
						></div>
					</div>
				</div>

				{/* Step content */}
				{currentStep === 1 && (
					<div className="bg-white rounded-xl shadow-lg p-6">
						<h2 className="text-2xl text-gray-900 font-semibold mb-2">Check Your Registration Status</h2>
						<p className="text-gray-600 text-sm mb-6">Enter your information below to look up your voter registration using official state sources.</p>

						{/* Registration Form */}
						<div className="md:grid grid-cols-1 md:grid-cols-3 space-y-4 md:space-y-0 gap-6 text-black">
							<div>
								<label className="font-semibold">First Name <span className="text-red-600">*</span></label>
								<Input
									type="text"
									name="firstName"
									value={engagementData.firstName}
									onChange={handleChange}
									placeholder="First Name"
									variant="primary"
									size="medium"
									className="mt-1"
									// debounce={0}
									required={true}
								/>
								{formErrors.firstName && <p className="text-red-600 text-xs mt-1">{formErrors.firstName}</p>}
							</div>
							<div>
								<label className="font-semibold">Last Name <span className="text-red-600">*</span></label>
								<Input
									type="text"
									name="lastName"
									value={engagementData.lastName}
									onChange={handleChange}
									placeholder="Last Name"
									variant="primary"
									size="medium"
									className="mt-1"
									// debounce={0}
									required
								/>
								{formErrors.lastName && <p className="text-red-600 text-xs mt-1">{formErrors.lastName}</p>}
							</div>
							<div>
								<label className="font-semibold">State <span className="text-red-600">*</span></label>
								<select
									name="state"
									value={engagementData.state}
									onChange={handleChange}
									className={`w-full border rounded px-3 py-2 mt-1 transition-all focus:border-red-600 bg-white ${formErrors.state ? "border-red-500" : "border-gray-400"}`}
									required
								>
									<option value="">Select State</option>
									{US_STATES.map((s) => (
										<option key={s.code} value={s.code}>{s.name}</option>
									))}
								</select>
								{formErrors.state && <p className="text-red-600 text-xs mt-1">{formErrors.state}</p>}
							</div>
							<div>
								<label className="font-semibold">Zipcode <span className="text-red-600">*</span></label>
								<Input
									type="text"
									name="zipcode"
									value={engagementData.zipcode}
									onChange={handleChange}
									placeholder="Zipcode"
									variant="primary"
									size="medium"
									className="mt-1"
									// debounce={0}
									required
								/>
								{formErrors.zipcode && <p className="text-red-600 text-xs mt-1">{formErrors.zipcode}</p>}
							</div>
							<div>
								<label className="font-semibold">City <span className="text-red-600">*</span></label>
								<Input
									type="text"
									name="city"
									value={engagementData.city}
									onChange={handleChange}
									placeholder="City"
									variant="primary"
									size="medium"
									className="mt-1"
									// debounce={0}
									required
								/>
								{formErrors.city && <p className="text-red-600 text-xs mt-1">{formErrors.city}</p>}
							</div>
							<div>
								<label className="font-semibold">Home Address <span className="text-red-600">*</span></label>
								<Input
									type="text"
									name="address"
									value={engagementData.address}
									onChange={handleChange}
									placeholder="123 Main St"
									variant="primary"
									size="medium"
									className="mt-1"
									// debounce={0}
									required
								/>
								{formErrors.address && <p className="text-red-600 text-xs mt-1">{formErrors.address}</p>}
							</div>
							<div>
								<label className="font-semibold">Phone Number</label>
								<Input
									type="text"
									name="phoneNumber"
									value={engagementData.phoneNumber}
									onChange={handleChange}
									placeholder="Phone Number (Optional)"
									variant="primary"
									size="medium"
									className="mt-1"
									required={false}
								/>
								{formErrors.phoneNumber && <p className="text-red-600 text-xs mt-1">{formErrors.phoneNumber}</p>}
							</div>
							<div>
								<label className="font-semibold">Email Address <span className="text-red-600">*</span></label>
								<Input
									type="email"
									name="email"
									value={engagementData.email}
									onChange={handleChange}
									placeholder="Email Address"
									variant="primary"
									size="medium"
									className="mt-1"
									// debounce={0}
									required
								/>
								{formErrors.email && <p className="text-red-600 text-xs mt-1">{formErrors.email}</p>}
							</div>
							<div>
								<label className="font-semibold">Date of Birth <span className="text-red-600">*</span></label>
								<Input
									type="date"
									name="dob"
									value={engagementData.dob}
									onChange={handleChange}
									variant="primary"
									size="medium"
									className="mt-1"
									// debounce={0}
									required
								/>
								{formErrors.dob && <p className="text-red-600 text-xs mt-1">{formErrors.dob}</p>}
							</div>
							<div className="flex items-center mt-4 space-x-8 col-span-2">
								<label className="font-semibold">Gender: <span className="text-red-600">*</span></label>
								<label className="flex items-center space-x-2">
									<input type="radio" name="gender" checked={engagementData.gender === "Male"} onChange={() => handleGender("Male")} />
									<span>Male</span>
								</label>
								<label className="flex items-center space-x-2">
									<input type="radio" name="gender" checked={engagementData.gender === "Female"} onChange={() => handleGender("Female")} />
									<span>Female</span>
								</label>
								{formErrors.gender && <p className="text-red-600 text-xs mt-1">{formErrors.gender}</p>}
							</div>

						</div>
						<p className="text-gray-600 text-sm mt-3">Your information is used only to perform this lookup and is not stored as a voter profile</p>

						{/* Election Reminders */}
						<div className="flex flex-col space-y-2 text-black mt-6">
							<label className="flex items-center space-x-2">
								<input
									type="checkbox"
									name="reminder"
									checked={engagementData.reminder}
									onChange={handleChange}
								/>
								<span className="font-semibold">Receive election reminders and action alerts updates from 123-Vote. <span className="text-red-600">*</span></span>
							</label>
							{/* SMS Consent as faint, tiny disclaimer */}
							<div className="text-xs text-gray-400 mt-1 pl-8 italic">
								By entering your phone number and pressing "Check your registration", you consent to receive periodic text and email about voting information support from 123-Vote. Message and data rates may apply. Text STOP to opt out at any time. You may unsubscribe from email at any time.
							</div>
						</div>

						<div className="flex justify-between mt-8">
							<Button
								label="Back"
								variant="secondary"
								onClick={previousStep}
								disabled={currentStep === 1}
							/>
							<Button
								label={registrationCheckMutation.isPending ? "Checking..." : "Check Status"}
								variant="primary"
								onClick={handleRegistrationCheck}
							// disabled={!isRegistrationFormValid || registrationCheckMutation.isPending}
							/>
						</div>

						{/* Compliance Note */}
						<div className="mt-6 pt-4 border-t border-gray-200">
							<p className="text-xs text-gray-500 text-center">
								Information is sent securely to public election systems or official state websites.
							</p>
						</div>
					</div>
				)}

				{currentStep === 2 && (
					<div className="bg-white rounded-xl shadow-lg p-10">
						{registrationResult?.redirectUrl ? (
							// State Registration Flow
							<>
								<div className="bg-rose-600 rounded-full p-2 flex justify-center items-center m-auto w-fit animate-pulse text-white">
									<TravelExploreIcon fontSize="large" />
								</div>
								<h1 className="text-2xl text-gray-900 font-semibold mb-2 capitalize text-center">Check my registration <br />on the official state site</h1>
								<h2 className="text-sm text-gray-900 font-semibold mb-2 text-center">You can only register using your state official website</h2>
								<p className="text-gray-600 text-xs text-center mb-6">
									You’ll be taken to the official state election website to check your registration status.
								</p>

								<div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
									<div className="flex items-start space-x-3">
										<svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										<div>
											<h3 className="font-semibold text-blue-900 mb-2"><ErrorOutlineIcon /> Important Information</h3>
											<ul className="text-sm text-blue-800 space-y-1">
												<li>• 123Vote does not register voters directly!</li>
												<li>• Your registration will be completed on a government-run site</li>
												<li>• 123Vote does not control or store state registration data</li>
												<li>• You can return after you're done</li>
											</ul>
										</div>
									</div>
								</div>

								<div className="space-y-4">
									<div className="p-4 bg-gray-50 rounded-lg">
										<p className="text-sm text-gray-600 mb-2">State Website:</p>
										<p className="font-medium text-sm truncate select-none text-gray-800">{registrationResult.redirectUrl}</p>
									</div>

									<div className="sm:flex hidden justify-between">
										<Button
											label="Back"
											variant="secondary"
											onClick={previousStep}
										/>
										<Button
											label="Continue to State Website"
											variant="primary"
											onClick={handleOpenStateWebsite}
										/>
									</div>
									<div className="sm:hidden flex gap-3 justify-between">
										<Button
											label="Back"
											variant="secondary"
											size="medium"
											onClick={previousStep}
										/>
										<Button
											label="Continue to State Website"
											variant="primary"
											size="large"
											onClick={handleOpenStateWebsite}
										/>
									</div>
								</div>
							</>
						) : (
							// This should not happen with current logic, but keeping as fallback
							<>
								<h2 className="text-2xl font-semibold mb-2">Engagement Step 2: Voter Information</h2>
								<p className="text-gray-600 mb-6">Collect additional voter information.</p>

								<div className="space-y-4">
									<div className="p-4 bg-gray-50 rounded-lg">
										<p className="text-sm text-gray-600">This step will collect voter details...</p>
									</div>
								</div>

								<div className="flex justify-between mt-8">
									<Button
										label="Back"
										variant="secondary"
										onClick={previousStep}
									/>
									<Button
										label="Next Step →"
										variant="primary"
										onClick={nextStep}
									/>
								</div>
							</>
						)}
					</div>
				)}

				{currentStep === 3 && currentFlow === 'reminders' && (
					<ElectionReminders
						engagementData={engagementData}
						updateEngagementData={updateEngagementData}
						onNext={nextStep}
						setShowReturnFlow={setShowReturnFlow}
						onPrevious={previousStep}
					/>
				)}

				{currentStep === 3 && currentFlow === 'dates' && (
					<ElectionDates
						isElectionLoading={isElectionLoading}
						electionError={electionError}
						electionData={electionData}
						engagementData={engagementData}
						onShowReturnFlow={() => setShowReturnFlow(true)}
						onNextStep={nextStep}
						onSetCurrentFlow={setCurrentFlow}
						onSetCurrentStep={setCurrentStep}
					/>
				)}

				{currentStep === 3 && currentFlow === 'polling' && (
					<div className="bg-white rounded-xl shadow-lg p-6">
						<h2 className="text-2xl text-gray-900 font-semibold mb-2">Find Your Polling Location</h2>
						<p className="text-gray-600 text-sm mb-6">
							Access official polling location information for your address.
						</p>

						{/* Map Container */}
						<div className="mb-6">
							<div className="h-96 rounded-lg overflow-hidden border border-gray-300">
								<MapContainer
									center={pollingLocations.length > 0 ? [pollingLocations[0].latitude, pollingLocations[0].longitude] : [30.2672, -97.7431]}
									zoom={pollingLocations.length > 0 ? 14 : 12}
									style={{ height: '100%', width: '100%' }}
								>
									<TileLayer
										url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
										attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
									/>
									{pollingLocations.map((location, index) => (
										<Marker key={index} position={[location.latitude, location.longitude]}>
											<Popup>
												<div className="p-2">
													<h3 className="font-semibold text-sm mb-1">{location.address.locationName}</h3>
													<p className="text-xs text-gray-600 mb-1">{location.address.line1}</p>
													<p className="text-xs text-gray-600 mb-1">{location.address.city}, {location.address.state} {location.address.zip}</p>
													{location.pollingHours && <p className="text-xs text-gray-500">Hours: {location.pollingHours}</p>}
												</div>
											</Popup>
										</Marker>
									))}
								</MapContainer>
							</div>
						</div>

						{/* Polling Locations List */}
						<div className="space-y-3 mb-6">
							<h3 className="font-semibold text-gray-900 mb-3">Nearby Polling Locations</h3>
							{isPollingLoading ? (
								<div className="flex items-center justify-center py-8">
									<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
									<span className="ml-3 text-gray-600">Finding polling locations...</span>
								</div>
							) : pollingLocations.length > 0 ? (
								pollingLocations.map((location, index) => (
									<div key={index} className="border border-gray-200 rounded-lg p-4">
										<div className="flex justify-between items-start">
											<div className="flex-1">
												<h4 className="font-medium text-gray-900 mb-1">{location.address.locationName}</h4>
												<p className="text-sm text-gray-600 mb-1">{location.address.line1}</p>
												<p className="text-sm text-gray-600 mb-1">{location.address.city}, {location.address.state} {location.address.zip}</p>
												{location.pollingHours && <p className="text-xs text-gray-500">Hours: {location.pollingHours}</p>}
											</div>
											<Button
												label="View Details"
												variant="secondary"
												size="small"
												onClick={() => {
													// Center map on this location
													const mapElement = document.querySelector('.leaflet-container');
													if (mapElement) {
														// This would require map ref implementation for full functionality
														console.log('Centering on location:', location);
													}
												}}
											/>
										</div>
									</div>
								))
							) : (
								<div className="text-center py-8 text-gray-500">
									<MapIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
									<p>No polling locations found for your address.</p>
									<button
										onClick={() => refetchPollingLocations()}
										className="mt-3 text-blue-600 hover:text-blue-800 text-sm font-medium"
									>
										Try Again
									</button>

									{pollingLocationsData?.data?.state?.[0]?.electionAdministrationBody?.votingLocationFinderUrl && (
										<div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
											<p className="text-sm text-blue-800 mb-2">
												For more polling location results, visit your state's official website:
											</p>
											<a
												href={pollingLocationsData.data.state[0].electionAdministrationBody.votingLocationFinderUrl}
												target="_blank"
												rel="noopener noreferrer"
												className="text-blue-600 hover:text-blue-800 underline text-sm font-medium"
											>
												{pollingLocationsData.data.state[0].electionAdministrationBody.votingLocationFinderUrl}
											</a>
										</div>
									)}
								</div>
							)}
						</div>

						{/* Compliance Note */}
						<div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
							<p className="text-xs text-gray-600">
								Polling information is provided by official election sources.
							</p>
						</div>

						<div className="flex justify-between">
							<Button
								label="Back"
								variant="secondary"
								onClick={() => setShowReturnFlow(true)}
							/>
							<Button
								label="Next Step →"
								variant="primary"
								onClick={nextStep}
							/>
						</div>
					</div>
				)}

				{currentStep === 4 && !engagementData.showConfirmation && (
					<VoterPledge
						engagementData={engagementData}
						updateEngagementData={updateEngagementData}
						onNext={handlePledgeSubmit}
						isSubmitting={pledgeToVoteMutation.isPending}
						onPrevious={previousStep}
					/>
				)}

				{currentStep === 4 && engagementData.showConfirmation && (
					<ConfirmationSuccess
						engagementData={engagementData}
						onFinish={endEngagement}
					/>
				)}
			</div>

			{/* Registration Status Page */}
			{showRegistrationStatusPage && (
				<div className="absolute top-0 inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl w-full max-w-2xl p-8 shadow-2xl">
						<div className="text-center">
							<h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">What did you find on the official site?</h2>
							<p className="text-gray-600 md:text-base text-sm mb-8">Please let us know your registration status</p>

							<div className="space-y-4 mb-8">
								<button
									onClick={() => handleRegistrationStatusSelection('registered')}
									className="w-full bg-white border border-gray-200 rounded-xl p-3 md:p-6 hover:shadow-md transition-shadow flex items-center justify-between group"
								>
									<div className="flex items-center gap-4">
										<div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
											<span className="text-green-600 font-bold">✓</span>
										</div>
										<div className="text-left">
											<h3 className="font-semibold text-gray-900">I'm registered</h3>
										</div>
									</div>
									<ArrowForwardIcon className="text-gray-400 group-hover:text-gray-600" />
								</button>

								<button
									onClick={() => handleRegistrationStatusSelection('not_registered')}
									className="w-full bg-white border border-gray-200 rounded-xl p-3 md:p-6 hover:shadow-md transition-shadow flex items-center justify-between group"
								>
									<div className="flex items-center gap-4">
										<div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
											<span className="text-red-600 font-bold">✗</span>
										</div>
										<div className="text-left">
											<h3 className="font-semibold text-gray-900">I'm not registered</h3>
										</div>
									</div>
									<ArrowForwardIcon className="text-gray-400 group-hover:text-gray-600" />
								</button>

								<button
									onClick={() => handleRegistrationStatusSelection('not_sure')}
									className="w-full bg-white border border-gray-200 rounded-xl p-3 md:p-6 hover:shadow-md transition-shadow flex items-center justify-between group"
								>
									<div className="flex items-center gap-4">
										<div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
											<span className="text-yellow-600 font-bold">!</span>
										</div>
										<div className="text-left">
											<h3 className="font-semibold text-gray-900">I'm not sure / I couldn't complete it</h3>
										</div>
									</div>
									<ArrowForwardIcon className="text-gray-400 group-hover:text-gray-600" />
								</button>
							</div>

							<div className="bg-gray-50 rounded-lg p-4">
								<label className="flex items-center space-x-3">
									<input
										type="checkbox"
										checked={contactOptInChecked}
										onChange={(e) => setContactOptInChecked(e.target.checked)}
										className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
									/>
									<span className="text-sm text-gray-700">
										Can we text or email you a link to official state site?
									</span>
								</label>
								{contactOptInChecked && (
									<div className="mt-3 text-xs text-gray-600 bg-white p-3 rounded border">
										<p><strong>State:</strong> {engagementData.state}</p>
										<p><strong>Email:</strong> {engagementData.email}</p>
										<p><strong>Phone:</strong> {engagementData.phoneNumber || 'Not provided'}</p>
									</div>
								)}
								<div className="text-xs text-gray-400 mt-2">
									By checking this box, you consent to receive periodic text and email about voting information support from 123-Vote. Message and data rates may apply. Text STOP to opt out at any time. You may unsubscribe from email at any time.
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Not Registered Modal */}
			{showNotRegisteredModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl w-full max-w-md p-8 shadow-2xl">
						<div className="text-center">
							<div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
								<span className="text-red-600 text-2xl font-bold">✗</span>
							</div>
							<h2 className="text-xl font-bold text-gray-900 mb-2">You're not registered yet</h2>
							<p className="text-gray-600 mb-6">You can register on your state's official website</p>

							<Button
								label="Register on your state site"
								variant="primary"
								size="extra-large"
								onClick={handleRegisterOnStateSite}
							/>

							<Button
								label="Cancel"
								variant="secondary"
								size="extra-large"
								additionalClassname={"mt-3"}
								onClick={() => setShowNotRegisteredModal(false)}
							/>
						</div>
					</div>
				</div>
			)}


			{/* Completion Check Modal */}
			{showCompletionCheckModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl w-full max-w-md p-8 shadow-2xl">
						<div className="text-center">
							<div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
								<ErrorOutlineIcon className="text-blue-600" fontSize="large" />
							</div>
							<h2 className="text-xl font-bold text-gray-900 mb-2">Did you complete registration?</h2>
							<p className="text-gray-600 mb-6">Let us know if you were able to complete your registration</p>

							<div className="space-y-3">
								<Button
									label="Yes, I completed registration"
									size="extra-large"
									variant="primary"
									onClick={() => handleCompletionCheckResponse(true)}
								/>

								<Button
									label="No, I couldn't complete it"
									size="extra-large"
									variant="secondary"
									onClick={() => handleCompletionCheckResponse(false)}
								/>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Contact Opt-In Page */}
			{showContactOptIn && (
				<div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl w-full max-w-md p-8 shadow-2xl">
						<div className="text-center">
							<div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
								<ErrorOutlineIcon className="text-yellow-600" fontSize="large" />
							</div>
							<h2 className="text-xl font-bold text-gray-900 mb-2">Need help with registration?</h2>
							<p className="text-gray-600 mb-6">We can send you a link to your state's official registration site</p>

							<div className="bg-gray-50 rounded-lg p-4 mb-6">
								<p className="text-sm font-medium text-gray-700 mb-3">Your information:</p>
								<p><strong>State:</strong> {engagementData.state}</p>
								<p><strong>Email:</strong> {engagementData.email}</p>
								<p><strong>Phone:</strong> {engagementData.phoneNumber || 'Not provided'}</p>
							</div>

							<label className="flex items-center space-x-3 mb-6">
								<input
									type="checkbox"
									checked={contactOptInChecked}
									onChange={(e) => setContactOptInChecked(e.target.checked)}
									disabled={contactOptInMutation.isPending}
									className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
								/>
								<span className="text-sm text-gray-700">
									Can we text or email you a link to official state site?
								</span>
							</label>

							<div className="text-xs text-gray-400 mb-6">
								By checking this box, you consent to receive periodic text and email about voting information support from 123-Vote. Message and data rates may apply. Text STOP to opt out at any time. You may unsubscribe from email at any time.
							</div>

							<div className="flex gap-3">
								<Button
									label="No, Proceed"
									size="extra-large"
									variant="secondary"
									onClick={handleContactOptInSkip}
									disabled={contactOptInMutation.isPending}
								/>
								<Button
									label={contactOptInMutation.isPending ? "Submitting..." : "Yes, Next"}
									size="extra-large"
									variant="primary"
									onClick={handleContactOptInSubmit}
									disabled={!contactOptInChecked || contactOptInMutation.isPending}
								/>
							</div>
						</div>
					</div>
				</div>
			)}


			{/* Return Flow Modal */}
			{showReturnFlow && (
				<div className="absolute inset-0 bg-black bg-opacity-50 z-[999999] flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl w-full max-w-2xl p-4 md:p-8 shadow-2xl">
						<div className="text-center">
							<div className="inline-flex items-center justify-center w-16 h-16 bg-blue-200 rounded-full mb-4">
								<CheckCircleIcon fontSize="large" className=" text-blue-600" />
							</div>
							<h2 className="text-sm font-medium text-gray-500">Welcome Back</h2>
							<h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">You are registered to Vote!</h2>
							<p className="text-gray-600 mb-8">What would you like to do next?</p>

							<div className="space-y-4">
								<div
									onClick={() => registrationResult?.onlineVoteByMailAllowed && handleReturnFlowOption('vbm')}
									className={`bg-white border border-gray-200 rounded-xl p-3 md:p-6 hover:shadow-md transition-shadow  flex items-center justify-between ${!registrationResult?.onlineVoteByMailAllowed ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
								>
									<div className="flex items-center gap-4">
										<div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
											<Mail className="text-blue-600" fontSize="medium" />
										</div>
										<div className="text-left">
											<h3 className="font-semibold text-gray-900">Vote by mail</h3>
											<p className="text-sm text-gray-600">Request on the state site to vote by mail</p>
										</div>
									</div>
									<ArrowForwardIcon className="text-gray-400" />
								</div>
								<div
									onClick={() => handleReturnFlowOption('polling')}
									className="bg-white border border-gray-200 rounded-xl p-3 md:p-6 hover:shadow-md transition-shadow cursor-pointer flex items-center justify-between"
								>
									<div className="flex items-center gap-4">
										<div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
											<MapIcon className="text-blue-600" fontSize="medium" />
										</div>
										<div className="text-left">
											<h3 className="font-semibold text-gray-900">Find My Polling Location</h3>
											<p className="text-sm text-gray-600">See where to cast your ballot.</p>
										</div>
									</div>
									<ArrowForwardIcon className="text-gray-400" />
								</div>

								<div
									onClick={() => handleReturnFlowOption('reminders')}
									className="bg-white border border-gray-200 rounded-xl p-3 md:p-6 hover:shadow-md transition-shadow cursor-pointer flex items-center justify-between"
								>
									<div className="flex items-center gap-4">
										<div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
											<NotificationsIcon className="text-green-600" fontSize="medium" />
										</div>
										<div className="text-left">
											<h3 className="font-semibold text-gray-900">Get Election Reminders</h3>
											<p className="text-sm text-gray-600">Never miss a deadline or date.</p>
										</div>
									</div>
									<ArrowForwardIcon className="text-gray-400" />
								</div>

								<div
									onClick={() => handleReturnFlowOption('dates')}
									className="bg-white border border-gray-200 rounded-xl p-3 md:p-6 hover:shadow-md transition-shadow cursor-pointer flex items-center justify-between"
								>
									<div className="flex items-center gap-4">
										<div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
											<CalendarMonthIcon className="text-purple-600" fontSize="medium" />
										</div>
										<div className="text-left">
											<h3 className="font-semibold text-gray-900">Learn About Voting Dates</h3>
											<p className="text-xs text-gray-500 italic">Get information about voting dates in your state.</p>
										</div>
									</div>
									<AddIcon className="text-gray-400" />
								</div>

								<div
									onClick={() => handleReturnFlowOption('exit')}
									className="bg-white border border-gray-200 rounded-xl p-3 md:p-6 hover:shadow-md transition-shadow cursor-pointer flex items-center justify-between"
								>
									<div className="flex items-center gap-4">
										<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
											<ArrowForwardIcon className="text-gray-600" fontSize="medium" />
										</div>
										<div className="text-left">
											<h3 className="font-semibold text-gray-900">Exit</h3>
											<p className="text-sm text-gray-600">Complete and exit</p>
										</div>
									</div>
									<ArrowForwardIcon className="text-gray-400" />
								</div>
							</div>
						</div>
					</div>
				</div>
			)}


			{/* VBM Completion Modal */}
			{showVBMCompletionModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl w-full max-w-md p-8 shadow-2xl">
						<div className="text-center">
							<div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
								<Mail className="text-blue-600" fontSize="large" />
							</div>
							<h2 className="text-xl font-bold text-gray-900 mb-2">What did you find on state site?</h2>
							<p className="text-gray-600 mb-6">Please let us know how it went</p>

							<div className="space-y-3 mb-6">
								<button
									onClick={() => handleVBMCompletionResponse('requested')}
									className="w-full px-4 py-3 border border-slate-300 bg-white text-gray-800 rounded-lg hover:bg-gray-100 transition-colors text-left flex items-center gap-3"
								>
									<CheckCircleIcon />
									I requested a mail ballot
								</button>
								<button
									onClick={() => handleVBMCompletionResponse('didnt_complete')}
									className="w-full px-4 py-3 border border-slate-300 bg-white text-gray-800 rounded-lg hover:bg-gray-100 transition-colors text-left flex items-center gap-3"
								>
									<ErrorOutlineIcon />
									I didn't complete it
								</button>
								<button
									onClick={() => handleVBMCompletionResponse('not_eligible')}
									className="w-full px-4 py-3 border border-slate-300 bg-white text-gray-800 rounded-lg hover:bg-gray-100 transition-colors text-left flex items-center gap-3"
								>
									<ErrorOutlineIcon />
									I wasn't eligible / couldn't continue
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* VBM Opt-in Modal */}
			{showVBMOptInModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl w-full max-w-md p-8 shadow-2xl">
						<div className="text-center">
							<div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
								<Mail className="text-blue-600" fontSize="large" />
							</div>
							<h2 className="text-xl font-bold text-gray-900 mb-2">Get Mail Ballot Updates</h2>
							<p className="text-gray-600 mb-6">Choose the updates you'd like to receive (optional)</p>

							<div className="space-y-3 mb-6 text-left">
								<label className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
									<input
										type="checkbox"
										checked={vbmOptIns.mailBallotReminders}
										onChange={(e) => handleVBMOptInChange('mailBallotReminders', e.target.checked)}
										className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
									/>
									<div>
										<span className="font-medium text-gray-900">Get mail ballot reminders</span>
										<p className="text-sm text-gray-600">Receive reminders about your mail ballot status</p>
									</div>
								</label>

								<label className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
									<input
										type="checkbox"
										checked={vbmOptIns.ballotTracking}
										onChange={(e) => handleVBMOptInChange('ballotTracking', e.target.checked)}
										className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
									/>
									<div>
										<span className="font-medium text-gray-900">Check ballot tracking info</span>
										<p className="text-sm text-gray-600">Track your mail ballot status and delivery</p>
									</div>
								</label>

								<label className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
									<input
										type="checkbox"
										checked={vbmOptIns.returnDeadlineReminders}
										onChange={(e) => handleVBMOptInChange('returnDeadlineReminders', e.target.checked)}
										className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
									/>
									<div>
										<span className="font-medium text-gray-900">Get return deadline reminders</span>
										<p className="text-sm text-gray-600">Get reminders about ballot return deadlines</p>
									</div>
								</label>
							</div>

							<button
								onClick={handleVBMOptInSubmit}
								className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
							>
								Finish
							</button>
						</div>
					</div>
				</div>
			)}

			{/* VBM Retry Modal */}
			{showVBMRetryModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl w-full max-w-md p-8 shadow-2xl">
						<div className="text-center">
							<div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
								<ErrorOutlineIcon className="text-yellow-600" fontSize="large" />
							</div>
							<h2 className="text-xl font-bold text-gray-900 mb-2">Request Not Completed</h2>
							<p className="text-gray-600 text-xs mb-6">
								It looks like your request was not completed. You must complete your request on the state form for your ballot to be sent.
							</p>

							<div className="mb-6 p-4 bg-gray-50 rounded-lg">
								<p className="text-sm text-gray-700 mb-2">State Ballot Request URL:</p>
								<a
									href={registrationResult?.voteByMailUrl}
									target="_blank"
									rel="noopener noreferrer"
									className="text-blue-600 hover:text-blue-800 text-sm break-all"
								>
									{registrationResult?.voteByMailUrl}
								</a>
							</div>

							<Button
								onClick={handleVBMRetryReturn}
								variant="primary"
								size="extra-large"
								label="Return to state site"
							/>

						</div>
					</div>
				</div>
			)}

			<div className="mt-8 pt-6 border-t border-gray-200">
				<p className="text-xs text-gray-500 text-center">
					123Vote is a civic engagement platform.
					<br />
					We do not endorse or oppose candidates, parties, or ballot measures.
					<br />
					Participation is always voluntary.
				</p>
			</div>
		</AdminLayout>
	);
}
